import { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Search, Printer, ArrowUpDown, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { PrintableReport } from "./PrintableReport";

type SortKey = "date" | "name" | "class";
type SortDir = "asc" | "desc";

interface Row {
  id: string;
  student_id: string;
  date: string;
  reg_no: string;
  name: string;
  class: string;
  semester: string;
  time_in: string;
  time_out: string | null;
  signature_url: string | null;
}

export function GateAttendanceReport() {
  const { hasRole } = useAuth();
  const canDelete = hasRole("superadmin");
  const [from, setFrom] = useState<Date | undefined>();
  const [to, setTo] = useState<Date | undefined>();
  const [semester, setSemester] = useState<string>("all");
  const [klass, setKlass] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [classes, setClasses] = useState<string[]>([]);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    supabase.from("students").select("class").then(({ data }) => {
      const list = [...new Set((data ?? []).map((s) => s.class).filter(Boolean) as string[])].sort();
      setClasses(list);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      let q = supabase
        .from("attendance_records")
        .select("id, attendance_date, attendance_type, scanned_at, time_out, registration_number, student_id, students!inner(first_name, last_name, student_number, class, current_semester, signature_url)")
        .in("attendance_type", ["general_entry", "general_exit", "gate"])
        .order("scanned_at", { ascending: true })
        .limit(2000);

      if (from) q = q.gte("attendance_date", format(from, "yyyy-MM-dd"));
      if (to) q = q.lte("attendance_date", format(to, "yyyy-MM-dd"));

      const { data } = await q;
      if (cancelled) return;

      // Pair entry/exit per student/day with strict time windows (local EAT)
      // Time In: 06:00–08:30  |  Time Out: 15:30–16:30
      const inEntryWindow = (iso: string) => {
        const d = new Date(iso);
        const m = d.getHours() * 60 + d.getMinutes();
        return m >= 6 * 60 && m <= 8 * 60 + 30;
      };
      const inExitWindow = (iso: string) => {
        const d = new Date(iso);
        const m = d.getHours() * 60 + d.getMinutes();
        return m >= 15 * 60 + 30 && m <= 16 * 60 + 30;
      };

      const grouped = new Map<string, Row>();
      for (const r of (data ?? []) as any[]) {
        const key = `${r.student_id}|${r.attendance_date}`;
        let existing = grouped.get(key);
        const isExit = r.attendance_type === "general_exit";
        const isEntry = r.attendance_type === "general_entry" || r.attendance_type === "gate";

        if (!existing) {
          existing = {
            id: r.id,
            student_id: r.student_id,
            date: r.attendance_date,
            reg_no: r.students?.student_number ?? r.registration_number ?? "—",
            name: `${r.students?.first_name ?? ""} ${r.students?.last_name ?? ""}`.trim() || "—",
            class: r.students?.class ?? "—",
            semester: r.students?.current_semester ?? "—",
            time_in: "",
            time_out: null,
            signature_url: r.students?.signature_url ?? null,
          };
          grouped.set(key, existing);
        }

        const t = Date.parse(r.scanned_at);
        if (isEntry && inEntryWindow(r.scanned_at)) {
          if (!existing.time_in || t < Date.parse(existing.time_in)) {
            existing.time_in = r.scanned_at;
          }
        }
        if (isExit && inExitWindow(r.scanned_at)) {
          if (!existing.time_out || t > Date.parse(existing.time_out)) {
            existing.time_out = r.scanned_at;
          }
        }
      }
      setRows(Array.from(grouped.values()).sort((a, b) => b.date.localeCompare(a.date)));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [from, to]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (semester !== "all" && r.semester !== semester) return false;
      if (klass !== "all" && r.class !== klass) return false;
      if (s && !r.name.toLowerCase().includes(s) && !r.reg_no.toLowerCase().includes(s)) return false;
      return true;
    });
    out.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      if (sortKey === "date") return (a.date.localeCompare(b.date)) * dir;
      if (sortKey === "name") return a.name.localeCompare(b.name) * dir;
      return a.class.localeCompare(b.class) * dir;
    });
    return out;
  }, [rows, semester, klass, search, sortKey, sortDir]);

  function toggleSort(k: SortKey) {
    if (k === sortKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  }

  async function handleDelete(row: Row) {
    const { error } = await supabase
      .from("attendance_records")
      .delete()
      .eq("student_id", row.student_id)
      .eq("attendance_date", row.date)
      .in("attendance_type", ["general_entry", "general_exit", "gate"]);
    if (error) {
      toast.error("Failed to delete record: " + error.message);
      return;
    }
    setRows((prev) => prev.filter((r) => !(r.student_id === row.student_id && r.date === row.date)));
    toast.success("Attendance record deleted");
  }

  const semesters = ["I", "II"];
  const filterSummary = [
    from ? `From: ${format(from, "PPP")}` : null,
    to ? `To: ${format(to, "PPP")}` : null,
    semester !== "all" ? `Semester: ${semester}` : null,
    klass !== "all" ? `Class: ${klass}` : null,
    search ? `Search: "${search}"` : null,
  ].filter(Boolean).join(" • ") || "All records";

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">From</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !from && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {from ? format(from, "PP") : "Any"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={from} onSelect={setFrom} className={cn("p-3 pointer-events-auto")} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !to && "text-muted-foreground")}>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {to ? format(to, "PP") : "Any"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={to} onSelect={setTo} className={cn("p-3 pointer-events-auto")} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Semester</Label>
              <Select value={semester} onValueChange={setSemester}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All semesters</SelectItem>
                  {semesters.map((s) => <SelectItem key={s} value={s}>Semester {s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Class</Label>
              <Select value={klass} onValueChange={setKlass}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All classes</SelectItem>
                  {classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input className="pl-8" placeholder="Name or reg no" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">{loading ? "Loading…" : `${filtered.length} record(s)`}</p>
            <Button size="sm" onClick={() => window.print()} disabled={filtered.length === 0}>
              <Printer className="w-4 h-4 mr-1" /> Export PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">SN</TableHead>
              <TableHead className="cursor-pointer" onClick={() => toggleSort("date")}>
                Date <ArrowUpDown className="inline h-3 w-3" />
              </TableHead>
              <TableHead>Reg No</TableHead>
              <TableHead className="cursor-pointer" onClick={() => toggleSort("name")}>
                Name <ArrowUpDown className="inline h-3 w-3" />
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => toggleSort("class")}>
                Class <ArrowUpDown className="inline h-3 w-3" />
              </TableHead>
              <TableHead>Time In</TableHead>
              <TableHead>Time Out</TableHead>
              <TableHead>Signature</TableHead>
              {canDelete && <TableHead className="w-12 text-right print:hidden">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={canDelete ? 9 : 8} className="text-center text-muted-foreground py-8">No records.</TableCell></TableRow>
            ) : filtered.map((r, i) => (
              <TableRow key={r.id}>
                <TableCell>{i + 1}</TableCell>
                <TableCell>{format(new Date(r.date), "PP")}</TableCell>
                <TableCell className="font-mono text-xs">{r.reg_no}</TableCell>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell>{r.class}</TableCell>
                <TableCell>{r.time_in ? format(new Date(r.time_in), "hh:mm a") : "—"}</TableCell>
                <TableCell>{r.time_out ? format(new Date(r.time_out), "hh:mm a") : "—"}</TableCell>
                <TableCell>
                  {r.signature_url ? (
                    <img src={r.signature_url} alt="signature" className="h-8 w-auto max-w-[80px] object-contain" />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                {canDelete && (
                  <TableCell className="text-right print:hidden">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete attendance record?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete all gate scans for <strong>{r.name}</strong> ({r.reg_no}) on {format(new Date(r.date), "PP")}. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(r)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <PrintableReport ref={printRef} title="Main Gate Attendance Report" filterSummary={filterSummary}>
        <table>
          <thead>
            <tr>
              <th>SN</th><th>Date</th><th>Reg No</th><th>Name</th><th>Class</th><th>Time In</th><th>Time Out</th><th>Signature</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => (
              <tr key={r.id}>
                <td>{i + 1}</td>
                <td>{format(new Date(r.date), "PP")}</td>
                <td>{r.reg_no}</td>
                <td>{r.name}</td>
                <td>{r.class}</td>
                <td>{r.time_in ? format(new Date(r.time_in), "hh:mm a") : "—"}</td>
                <td>{r.time_out ? format(new Date(r.time_out), "hh:mm a") : "—"}</td>
                <td>{r.signature_url ? <img src={r.signature_url} alt="sig" style={{ height: 24, maxWidth: 80, objectFit: "contain" }} /> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PrintableReport>
    </div>
  );
}
